/**
 * Incoming Fax State & Notification Service
 *
 * Requirements:
 * - Surgical tracking for the global "Incoming Fax" header action
 * - Normal state when unread count = 0: no badge, no dot, no number, no extra glow, no animation, no sound
 * - Arrival event when new unread fax arrives (unread count increases N -> N+1 or greater):
 *   1. Soft purple pulse / perimeter shimmer (approx 1-2 seconds, restrained, no layout movement)
 *   2. Single subtle notification ding (via pulseAudio, respecting Pulse Sound preference)
 *   3. Settles into steady unread state (compact count badge, slightly brighter purple border/glow)
 * - DO NOT REPLAY ON REFRESH: On page load/remount/login, existing unread faxes render in steady unread
 *   state without triggering the arrival pulse or ding.
 * - When all unread faxes are opened/reviewed according to Fax Inbox workflow: unread count = 0, badge
 *   disappears completely, extra illumination disappears, returns to normal approved state.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { FaxRecord } from '../types';
import { playIncomingFaxDing } from './pulseAudio';

const STORAGE_KEY_REVIEWED = 'allegiant_reviewed_fax_ids';
const STORAGE_KEY_KNOWN = 'allegiant_known_fax_ids';
const EVENT_NAME = 'allegiant_incoming_fax_update';

interface IncomingFaxState {
  unreadCount: number;
  isPulsing: boolean;
  incomingFaxes: FaxRecord[];
}

let globalState: IncomingFaxState = {
  unreadCount: 0,
  isPulsing: false,
  incomingFaxes: [],
};

let hasInitialized = false;
let isFetching = false;
let pulseTimer: any = null;

function getStorageSet(key: string): Set<string> {
  if (typeof window === 'undefined' || !window.localStorage) return new Set();
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed) : new Set();
  } catch {
    return new Set();
  }
}

function saveStorageSet(key: string, set: Set<string>): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    localStorage.setItem(key, JSON.stringify(Array.from(set)));
  } catch {
    // Fail silently
  }
}

function emitUpdate(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(EVENT_NAME, {
      detail: { ...globalState },
    })
  );
}

/**
 * Triggers one short restrained visual arrival attention event (~1.6 seconds)
 * and plays one subtle notification ding (if sound is not OFF).
 */
export function triggerIncomingFaxArrivalPulse(): void {
  // Clear any existing pulse timer
  if (pulseTimer) {
    clearTimeout(pulseTimer);
  }

  // 1. Play one subtle notification ding
  try {
    playIncomingFaxDing();
  } catch {
    // Fail silently
  }

  // 2. Set visual attention pulse
  globalState = {
    ...globalState,
    isPulsing: true,
  };
  emitUpdate();

  // 3. Revert to steady unread state after 1.6s (no continuous animation)
  pulseTimer = setTimeout(() => {
    globalState = {
      ...globalState,
      isPulsing: false,
    };
    emitUpdate();
    pulseTimer = null;
  }, 1600);
}

/**
 * Query faxes from backend API and evaluate unread status.
 * Distinguishes existing unread faxes from newly arrived faxes.
 */
export async function syncIncomingFaxes(): Promise<IncomingFaxState> {
  if (isFetching) return globalState;
  isFetching = true;

  try {
    const res = await fetch('/api/communications/faxes');
    if (!res.ok) throw new Error('Failed to fetch faxes');
    const allFaxes: FaxRecord[] = await res.json();

    const incoming = (allFaxes || []).filter(
      (f) => f && f.direction === 'INCOMING'
    );
    const reviewedIds = getStorageSet(STORAGE_KEY_REVIEWED);
    const knownIds = getStorageSet(STORAGE_KEY_KNOWN);

    // Unread incoming faxes: incoming, not attached to client, not in reviewed list
    const unreadFaxes = incoming.filter(
      (f) => !f.client_id && !reviewedIds.has(f.id)
    );
    const currentUnreadCount = unreadFaxes.length;
    const previousUnreadCount = globalState.unreadCount;

    if (!hasInitialized) {
      // FIRST MOUNT / REFRESH BASELINE:
      // Record all existing incoming faxes into knownIds so we never replay pulse/ding on refresh
      incoming.forEach((f) => knownIds.add(f.id));
      saveStorageSet(STORAGE_KEY_KNOWN, knownIds);
      hasInitialized = true;

      // Steady state without pulse or ding
      globalState = {
        unreadCount: currentUnreadCount,
        isPulsing: false,
        incomingFaxes: incoming,
      };
      emitUpdate();
      return globalState;
    }

    // SUBSEQUENT SYNC / POLLING:
    // Identify truly new incoming unread faxes that were never previously known
    const newlyArrived = unreadFaxes.filter((f) => !knownIds.has(f.id));

    if (newlyArrived.length > 0 && currentUnreadCount > previousUnreadCount) {
      // Record newly arrived faxes as known
      newlyArrived.forEach((f) => knownIds.add(f.id));
      saveStorageSet(STORAGE_KEY_KNOWN, knownIds);

      globalState = {
        ...globalState,
        unreadCount: currentUnreadCount,
        incomingFaxes: incoming,
      };

      // Trigger one-time arrival attention pulse and subtle ding
      triggerIncomingFaxArrivalPulse();
    } else {
      // Normal state update (e.g. steady count or decrease)
      // Ensure all incoming IDs are known
      incoming.forEach((f) => knownIds.add(f.id));
      saveStorageSet(STORAGE_KEY_KNOWN, knownIds);

      globalState = {
        ...globalState,
        unreadCount: currentUnreadCount,
        incomingFaxes: incoming,
      };
      emitUpdate();
    }

    return globalState;
  } catch {
    return globalState;
  } finally {
    isFetching = false;
  }
}

/**
 * Mark a specific incoming fax as reviewed/read.
 */
export function markFaxReviewed(faxId: string): void {
  if (!faxId) return;
  const reviewedIds = getStorageSet(STORAGE_KEY_REVIEWED);
  if (!reviewedIds.has(faxId)) {
    reviewedIds.add(faxId);
    saveStorageSet(STORAGE_KEY_REVIEWED, reviewedIds);
  }

  // Recalculate unread count with current cached faxes
  const unread = globalState.incomingFaxes.filter(
    (f) => !f.client_id && !reviewedIds.has(f.id)
  );
  globalState = {
    ...globalState,
    unreadCount: unread.length,
    isPulsing: unread.length === 0 ? false : globalState.isPulsing,
  };
  emitUpdate();
}

/**
 * Mark all incoming faxes as opened/reviewed (canonical Fax Inbox review workflow).
 * Unread count becomes 0, badge and extra illumination disappear completely.
 */
export function markAllIncomingFaxesReviewed(): void {
  const reviewedIds = getStorageSet(STORAGE_KEY_REVIEWED);
  globalState.incomingFaxes.forEach((f) => {
    if (f.direction === 'INCOMING') {
      reviewedIds.add(f.id);
    }
  });
  saveStorageSet(STORAGE_KEY_REVIEWED, reviewedIds);

  if (pulseTimer) {
    clearTimeout(pulseTimer);
    pulseTimer = null;
  }

  globalState = {
    ...globalState,
    unreadCount: 0,
    isPulsing: false,
  };
  emitUpdate();
}

/**
 * React hook for consuming Incoming Fax notification state.
 */
export function useIncomingFaxState() {
  const [state, setState] = useState<IncomingFaxState>(globalState);
  const pollTimerRef = useRef<any>(null);

  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const detail = (e as CustomEvent<IncomingFaxState>).detail;
      if (detail) {
        setState({ ...detail });
      }
    };

    window.addEventListener(EVENT_NAME, handleUpdate);

    // Initial fetch on mount
    syncIncomingFaxes();

    // Background polling every 20 seconds
    pollTimerRef.current = setInterval(() => {
      syncIncomingFaxes();
    }, 20000);

    // Refetch on window focus
    const handleFocus = () => {
      syncIncomingFaxes();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener(EVENT_NAME, handleUpdate);
      window.removeEventListener('focus', handleFocus);
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
      }
    };
  }, []);

  const markAsReviewed = useCallback((faxId: string) => {
    markFaxReviewed(faxId);
  }, []);

  const markAllReviewed = useCallback(() => {
    markAllIncomingFaxesReviewed();
  }, []);

  const refresh = useCallback(() => {
    return syncIncomingFaxes();
  }, []);

  return {
    unreadCount: state.unreadCount,
    isPulsing: state.isPulsing,
    incomingFaxes: state.incomingFaxes,
    markAsReviewed,
    markAllReviewed,
    refresh,
  };
}

// Attach debugging helpers to window for easy developer/QA verification
if (typeof window !== 'undefined') {
  (window as any).__triggerIncomingFaxPulse = triggerIncomingFaxArrivalPulse;
  (window as any).__syncIncomingFaxes = syncIncomingFaxes;
  (window as any).__markAllFaxesReviewed = markAllIncomingFaxesReviewed;
  (window as any).__resetFaxReadState = () => {
    localStorage.removeItem(STORAGE_KEY_REVIEWED);
    localStorage.removeItem(STORAGE_KEY_KNOWN);
    hasInitialized = false;
    syncIncomingFaxes();
  };
}
